import React from "react"
import axios from "axios"
import { connect } from "react-redux"
import { withTranslation } from "react-i18next"
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Row,
  Table,
} from "reactstrap"
import * as xlsx from "xlsx"

import { Loading, PageContainer } from "../../components"
import { autofitColumns } from "../../lib/excel"
import { fetchResources, fail, pushError } from "../../actions"

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100
const fmt = (n) => round2(n).toFixed(2)

// customer price of a row after discount
const rowUnitPrice = (row) =>
  row.type === "azure"
    ? round2((row.consumption || 0) * (1 - (row.discountPercent || 0) / 100))
    : round2((row.listPrice || 0) * (1 - (row.discountPercent || 0) / 100))

const rowTotal = (row) =>
  row.type === "azure"
    ? rowUnitPrice(row)
    : round2(rowUnitPrice(row) * (row.quantity || 0))

const rowCost = (row) =>
  row.type === "azure"
    ? round2((row.consumption || 0) * 0.85)
    : round2((row.purchasePrice || 0) * (row.quantity || 0))

const rowMargin = (row) => round2(rowTotal(row) - rowCost(row))

// monthly equivalent of a row's total (annual billing / 12, one-time / term)
const rowMonthly = (row) => {
  if (row.type === "azure" || row.billingCycle === "monthly")
    return rowTotal(row)
  if (row.billingCycle === "annual") return round2(rowTotal(row) / 12)
  if (row.termDuration > 0) return round2(rowTotal(row) / row.termDuration)
  return rowTotal(row)
}

const emptyQuote = () => ({
  id: null,
  name: "",
  customerId: "",
  customerName: "",
  rows: [],
  notes: "",
})

class QuoteGenerator extends React.Component {
  state = {
    loading: true,
    catalog: [],
    quotes: [],
    quote: emptyQuote(),
    search: "",
    saving: false,
    saved: false,
    checking: false,
    checked: false,
    priceCheck: {}, // rowIndex -> { status: "ok"|"changed"|"missing", listPrice, purchasePrice }
  }

  componentDidMount = async () => {
    this.props.fetchResources({ resource: "customers" })
    try {
      const [cat, quotes] = await Promise.all([
        axios.get("/quotecatalog"),
        axios.get("/quotes"),
      ])
      this.setState({
        loading: false,
        catalog: cat.data.quotecatalog || [],
        quotes: quotes.data.quotes || [],
      })
    } catch (error) {
      this.props.fail(null, error).then(() => this.setState({ loading: false }))
    }
  }

  refreshQuotes = async () => {
    const { data } = await axios.get("/quotes")
    this.setState({ quotes: data.quotes || [] })
  }

  setQuote = (patch) =>
    this.setState({
      quote: { ...this.state.quote, ...patch },
      saved: false,
      checked: false,
      priceCheck: {},
    })

  setRow = (index, patch) => {
    const rows = this.state.quote.rows.map((r, i) =>
      i === index ? { ...r, ...patch } : r
    )
    this.setQuote({ rows })
  }

  removeRow = (index) =>
    this.setQuote({ rows: this.state.quote.rows.filter((_, i) => i !== index) })

  addProductRow = (product, sku, offer) => {
    this.setQuote({
      rows: [
        ...this.state.quote.rows,
        {
          type: "product",
          productTitle: product.productTitle,
          skuTitle: sku.title,
          productId: product.productId,
          skuId: sku.skuId,
          termDuration: offer.termDuration,
          billingCycle: offer.billingPlan,
          quantity: 1,
          listPrice: offer.erpPrice,
          purchasePrice: offer.unitPrice,
          discountPercent: 0,
        },
      ],
      search: undefined,
    })
    this.setState({ search: "" })
  }

  addAzureRow = () => {
    this.setQuote({
      rows: [
        ...this.state.quote.rows,
        {
          type: "azure",
          productTitle: "Azure",
          billingCycle: "monthly",
          quantity: 1,
          consumption: 0,
          discountPercent: 0,
        },
      ],
    })
  }

  save = async () => {
    const { quote } = this.state
    const { t } = this.props
    if (!quote.name.trim()) {
      this.props.pushError(t("_quoteNameRequired"))
      return
    }
    this.setState({ saving: true })
    try {
      const customer = this.props.customers.customers.find(
        (c) => c.id === quote.customerId
      )
      const body = {
        name: quote.name,
        customerId: quote.customerId || null,
        customerName: customer ? customer.companyName : quote.customerName,
        rows: quote.rows,
        notes: quote.notes,
      }
      const { data } = quote.id
        ? await axios.put(`/quotes/${quote.id}`, body)
        : await axios.post("/quotes", body)
      this.setState({
        saving: false,
        saved: true,
        quote: { ...quote, id: data.quote.id },
      })
      this.refreshQuotes()
    } catch (error) {
      this.props.fail(null, error).then(() => this.setState({ saving: false }))
    }
  }

  loadQuote = async (id) => {
    try {
      const { data } = await axios.get(`/quotes/${id}`)
      const q = data.quote
      this.setState({
        quote: {
          id: q.id,
          name: q.name,
          customerId: q.customerId || "",
          customerName: q.customerName || "",
          rows: q.rows || [],
          notes: q.notes || "",
        },
        saved: true,
        checked: false,
        priceCheck: {},
      })
    } catch (error) {
      this.props.fail(null, error)
    }
  }

  deleteQuote = async (id) => {
    const { t } = this.props
    if (!window.confirm(t("_deleteQuoteConfirm"))) return
    try {
      await axios.delete(`/quotes/${id}`)
      if (this.state.quote.id === id) {
        this.setState({ quote: emptyQuote() })
      }
      this.refreshQuotes()
    } catch (error) {
      this.props.fail(null, error)
    }
  }

  // Double-check quote prices live against the Microsoft pricing API
  checkPrices = async () => {
    const productRows = this.state.quote.rows.filter(
      (r) => r.type === "product"
    )
    if (productRows.length === 0) return
    this.setState({ checking: true, checked: false, priceCheck: {} })
    try {
      const items = productRows.map((r) => ({
        productId: r.productId,
        skuId: r.skuId,
      }))
      const { data } = await axios.post("/quotecheck", { items })
      const priceCheck = {}
      this.state.quote.rows.forEach((row, i) => {
        if (row.type !== "product") return
        const fresh = (data.prices || []).find(
          (p) =>
            p.productId.toUpperCase() === (row.productId || "").toUpperCase() &&
            p.skuId.toUpperCase() === (row.skuId || "").toUpperCase() &&
            p.billingPlan === row.billingCycle &&
            p.termDuration === row.termDuration
        )
        if (!fresh) {
          priceCheck[i] = { status: "missing" }
        } else if (
          round2(fresh.erpPrice) !== round2(row.listPrice) ||
          round2(fresh.unitPrice) !== round2(row.purchasePrice)
        ) {
          priceCheck[i] = {
            status: "changed",
            listPrice: fresh.erpPrice,
            purchasePrice: fresh.unitPrice,
          }
        } else {
          priceCheck[i] = { status: "ok" }
        }
      })
      this.setState({ checking: false, checked: true, priceCheck })
    } catch (error) {
      this.props.fail(null, error).then(() => this.setState({ checking: false }))
    }
  }

  applyCheckedPrice = (index) => {
    const check = this.state.priceCheck[index]
    if (!check || check.status !== "changed") return
    this.setRow(index, {
      listPrice: check.listPrice,
      purchasePrice: check.purchasePrice,
    })
  }

  totals = () => {
    const rows = this.state.quote.rows
    const monthly = rows
      .filter((r) => r.billingCycle === "monthly" || r.type === "azure")
      .reduce((s, r) => s + rowTotal(r), 0)
    const annual = rows
      .filter((r) => r.type === "product" && r.billingCycle === "annual")
      .reduce((s, r) => s + rowTotal(r), 0)
    const margin = rows.reduce((s, r) => s + rowMargin(r), 0)
    const monthlyEquivalent = rows.reduce((s, r) => s + rowMonthly(r), 0)
    return {
      monthly: round2(monthly),
      annual: round2(annual),
      margin: round2(margin),
      monthlyEquivalent: round2(monthlyEquivalent),
    }
  }

  exportExcel = () => {
    const { t } = this.props
    const { quote } = this.state
    const rows = quote.rows.map((r) => ({
      [t("_product")]: r.type === "azure" ? "Azure" : r.productTitle,
      SKU: r.skuTitle || "",
      [t("_billingPlan")]:
        r.type === "azure"
          ? "monthly"
          : `${r.termDuration}kk / ${r.billingCycle}`,
      [t("_quantity")]: r.type === "azure" ? 1 : r.quantity,
      [t("_listPrice")]: r.type === "azure" ? r.consumption : r.listPrice,
      [t("_purchasePrice")]:
        r.type === "azure" ? round2((r.consumption || 0) * 0.85) : r.purchasePrice,
      [t("_discountPercent")]: r.discountPercent,
      [t("_customerPrice")]: rowUnitPrice(r),
      [t("_rowTotal")]: rowTotal(r),
      [t("_margin")]: rowMargin(r),
    }))
    const worksheet = xlsx.utils.json_to_sheet(rows)
    autofitColumns(rows, worksheet)
    const workbook = xlsx.utils.book_new()
    xlsx.utils.book_append_sheet(workbook, worksheet, "Tarjous")
    const blob = new Blob([xlsx.write(workbook, { type: "array" })], {
      type: "application/octet-stream",
    })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = `${quote.name || "tarjous"}.xlsx`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  // customer-facing printable view (browser's print-to-PDF); no cost/margin data
  printPdf = () => {
    const { t } = this.props
    const { quote } = this.state
    const customer = this.props.customers.customers.find(
      (c) => c.id === quote.customerId
    )
    const customerName = customer ? customer.companyName : quote.customerName
    const totals = this.totals()
    const rowsHtml = quote.rows
      .map(
        (r) => `<tr>
          <td>${r.type === "azure" ? "Azure-kulutus (arvio)" : `${r.productTitle}${r.skuTitle ? " - " + r.skuTitle : ""}`}</td>
          <td>${r.type === "azure" ? "kk" : `${r.termDuration}kk / ${r.billingCycle}`}</td>
          <td style="text-align:right">${r.type === "azure" ? 1 : r.quantity}</td>
          <td style="text-align:right">${fmt(rowUnitPrice(r))} €</td>
          <td style="text-align:right">${fmt(rowTotal(r))} €</td>
        </tr>`
      )
      .join("")
    const w = window.open("", "_blank")
    w.document.write(`<!DOCTYPE html><html><head><title>${quote.name}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 40px; color: #222; }
        h1 { font-size: 22px; } h2 { font-size: 15px; color: #555; font-weight: normal; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th, td { border-bottom: 1px solid #ddd; padding: 8px; text-align: left; font-size: 13px; }
        th { background: #f5f5f5; }
        .totals { margin-top: 16px; font-size: 14px; }
        .footer { margin-top: 40px; font-size: 11px; color: #777; }
      </style></head><body>
      <div style="font-size:18px;font-weight:700;margin-bottom:8px">Cinia</div>
      <h1>${quote.name}</h1>
      <h2>${customerName || ""} &mdash; ${new Date().toLocaleDateString("fi-FI")}</h2>
      <table><thead><tr>
        <th>${t("_product")}</th><th>${t("_billingPlan")}</th>
        <th style="text-align:right">${t("_quantity")}</th>
        <th style="text-align:right">á (alv 0%)</th>
        <th style="text-align:right">${t("_rowTotal")}</th>
      </tr></thead><tbody>${rowsHtml}</tbody></table>
      <div class="totals">
        ${totals.monthly ? `<div><b>${t("_totalPerMonth")}: ${fmt(totals.monthly)} €</b></div>` : ""}
        ${totals.annual ? `<div><b>${t("_totalPerYear")}: ${fmt(totals.annual)} €</b></div>` : ""}
        <div><b>${t("_monthlyEquivalent")}: ${fmt(totals.monthlyEquivalent)} €/kk</b></div>
      </div>
      ${quote.notes ? `<p>${quote.notes}</p>` : ""}
      <div class="footer">Cinia Oy &middot; Hinnat ALV 0 %. Azure-kulutus laskutetaan toteuman mukaan.</div>
      <script>window.print()</script>
      </body></html>`)
    w.document.close()
  }

  render() {
    const { t } = this.props
    const { quote, catalog, search, priceCheck } = this.state
    const totals = this.totals()
    const searchResults =
      search.trim().length >= 2
        ? catalog
            .filter((p) =>
              p.productTitle.toLowerCase().includes(search.toLowerCase())
            )
            .slice(0, 8)
        : []

    return (
      <PageContainer title={t("_quoteGenerator")} loading={this.state.loading}>
        <Row>
          <Col md="3">
            <h5>{t("_savedQuotes")}</h5>
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
              {this.state.quotes.map((q) => (
                <Card key={q.id} className="mb-1">
                  <CardBody className="p-2 d-flex justify-content-between align-items-center">
                    <span
                      style={{ cursor: "pointer" }}
                      onClick={() => this.loadQuote(q.id)}
                    >
                      <b>{q.name}</b>
                      <br />
                      <small>
                        {q.customerName || "-"} ·{" "}
                        {new Date(q.updatedAt).toLocaleDateString()}
                      </small>
                    </span>
                    <Button
                      close
                      onClick={() => this.deleteQuote(q.id)}
                      title={t("_delete")}
                    />
                  </CardBody>
                </Card>
              ))}
            </div>
            <Button
              block
              color="secondary"
              className="mt-2"
              onClick={() =>
                this.setState({
                  quote: emptyQuote(),
                  saved: false,
                  checked: false,
                  priceCheck: {},
                })
              }
            >
              {t("_newQuote")}
            </Button>
          </Col>
          <Col md="9">
            <Row className="mb-2">
              <Col md="5">
                <Input
                  placeholder={t("_quoteName")}
                  value={quote.name}
                  onChange={(e) => this.setQuote({ name: e.target.value })}
                />
              </Col>
              <Col md="4">
                <Input
                  type="select"
                  value={quote.customerId}
                  onChange={(e) => this.setQuote({ customerId: e.target.value })}
                >
                  <option value="">{`- ${t("_customer")} -`}</option>
                  {this.props.customers.customers.map((c) => (
                    <option value={c.id} key={c.id}>
                      {c.companyName}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md="3">
                {this.state.saving ? (
                  <Loading small />
                ) : (
                  <Button
                    block
                    color={this.state.saved ? "success" : "primary"}
                    onClick={this.save}
                  >
                    {this.state.saved ? t("_savedOk") : t("_save")}
                  </Button>
                )}
              </Col>
            </Row>

            <Input
              placeholder={t("_searchProducts")}
              value={search}
              onChange={(e) => this.setState({ search: e.target.value })}
            />
            {searchResults.map((p) => (
              <Card key={p.productId} className="mt-1">
                <CardBody className="p-2">
                  <b>{p.productTitle}</b>
                  {p.skus.map((sku) => (
                    <div key={sku.skuId} className="ml-2">
                      <small>{sku.title}</small>{" "}
                      {sku.offers.map((o, i) => (
                        <Button
                          key={i}
                          size="sm"
                          color="outline-primary"
                          className="m-1"
                          onClick={() => this.addProductRow(p, sku, o)}
                        >
                          {`${o.termDuration}kk/${o.billingPlan} ${fmt(
                            o.erpPrice
                          )}€`}
                        </Button>
                      ))}
                    </div>
                  ))}
                </CardBody>
              </Card>
            ))}

            <div className="mt-2 mb-2">
              <Button size="sm" color="info" onClick={this.addAzureRow}>
                {t("_addAzureRow")}
              </Button>{" "}
              <Button
                size="sm"
                color="warning"
                onClick={this.checkPrices}
                disabled={
                  this.state.checking ||
                  quote.rows.filter((r) => r.type === "product").length === 0
                }
              >
                {this.state.checking ? t("_checkingPrices") : t("_checkPrices")}
              </Button>{" "}
              {this.state.checked &&
                Object.values(priceCheck).every((c) => c.status === "ok") && (
                  <Badge color="success">{t("_pricesOk")}</Badge>
                )}
            </div>

            <Table size="sm">
              <thead>
                <tr>
                  <th>{t("_product")}</th>
                  <th>{t("_billingPlan")}</th>
                  <th>{t("_quantity")}</th>
                  <th>{t("_listPrice")}</th>
                  <th>{t("_purchasePrice")}</th>
                  <th>{t("_discountPercent")}</th>
                  <th>{t("_customerPrice")}</th>
                  <th>{t("_rowTotal")}</th>
                  <th>{`${t("_monthlyEquivalent")} €/kk`}</th>
                  <th>{t("_margin")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {quote.rows.map((row, i) => (
                  <tr key={i}>
                    <td>
                      {row.type === "azure"
                        ? "Azure (€/kk)"
                        : `${row.productTitle} - ${row.skuTitle}`}
                      {priceCheck[i]?.status === "changed" && (
                        <div>
                          <Badge color="danger">
                            {`${t("_priceChanged")}: ${fmt(
                              priceCheck[i].listPrice
                            )}€`}
                          </Badge>{" "}
                          <Button
                            size="sm"
                            color="link"
                            className="p-0"
                            onClick={() => this.applyCheckedPrice(i)}
                          >
                            {t("_applyNewPrice")}
                          </Button>
                        </div>
                      )}
                      {priceCheck[i]?.status === "missing" && (
                        <Badge color="warning">{t("_priceNotFound")}</Badge>
                      )}
                      {priceCheck[i]?.status === "ok" && (
                        <Badge color="success">OK</Badge>
                      )}
                    </td>
                    <td>
                      {row.type === "azure"
                        ? "kk"
                        : `${row.termDuration}kk/${row.billingCycle}`}
                    </td>
                    <td style={{ width: 80 }}>
                      {row.type === "azure" ? (
                        "-"
                      ) : (
                        <Input
                          bsSize="sm"
                          type="number"
                          min={1}
                          value={row.quantity}
                          onChange={(e) =>
                            this.setRow(i, { quantity: Number(e.target.value) })
                          }
                        />
                      )}
                    </td>
                    <td style={{ width: 110 }}>
                      {row.type === "azure" ? (
                        <Input
                          bsSize="sm"
                          type="number"
                          min={0}
                          value={row.consumption}
                          onChange={(e) =>
                            this.setRow(i, {
                              consumption: Number(e.target.value),
                            })
                          }
                        />
                      ) : (
                        fmt(row.listPrice)
                      )}
                    </td>
                    <td>
                      {row.type === "azure"
                        ? fmt((row.consumption || 0) * 0.85)
                        : fmt(row.purchasePrice)}
                    </td>
                    <td style={{ width: 90 }}>
                      <Input
                        bsSize="sm"
                        type="number"
                        step={0.1}
                        value={row.discountPercent}
                        onChange={(e) =>
                          this.setRow(i, {
                            discountPercent: Number(e.target.value),
                          })
                        }
                      />
                    </td>
                    <td>{fmt(rowUnitPrice(row))}</td>
                    <td>
                      <b>{fmt(rowTotal(row))}</b>
                    </td>
                    <td>{fmt(rowMonthly(row))}</td>
                    <td
                      className={
                        rowMargin(row) < 0 ? "text-danger" : "text-success"
                      }
                    >
                      {fmt(rowMargin(row))}
                    </td>
                    <td>
                      <Button close onClick={() => this.removeRow(i)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {quote.rows.length > 0 && (
              <>
                <Alert color="light">
                  {totals.monthly > 0 && (
                    <div>
                      <b>
                        {t("_totalPerMonth")}: {fmt(totals.monthly)} €
                      </b>
                    </div>
                  )}
                  {totals.annual > 0 && (
                    <div>
                      <b>
                        {t("_totalPerYear")}: {fmt(totals.annual)} €
                      </b>
                    </div>
                  )}
                  <div>
                    <b>
                      {t("_monthlyEquivalent")}: {fmt(totals.monthlyEquivalent)}{" "}
                      €/kk
                    </b>
                  </div>
                  <div>
                    {t("_margin")}: {fmt(totals.margin)} €
                  </div>
                </Alert>
                <Input
                  type="textarea"
                  placeholder={t("_notes")}
                  value={quote.notes}
                  onChange={(e) => this.setQuote({ notes: e.target.value })}
                  className="mb-2"
                />
                <Button color="primary" className="mr-2" onClick={this.printPdf}>
                  {t("_printPdf")}
                </Button>
                <Button color="secondary" onClick={this.exportExcel}>
                  {t("_downloadExcel")}
                </Button>
              </>
            )}
          </Col>
        </Row>
      </PageContainer>
    )
  }
}

export default connect((s) => ({ customers: s.customers }), {
  fetchResources,
  fail,
  pushError,
})(withTranslation()(QuoteGenerator))
